import React, { useState, useEffect } from 'react';
import { extractLogsService } from '../services/api';
import { ErrorState, ErrorBanner } from './ErrorMessage';
import { getErrorMessage } from '../utils/errors';
import { formatDay } from '../utils/format';
import StatusDonutChart from './charts/StatusDonutChart';
import DurationLineChart from './charts/DurationLineChart';
import FileTypeDurationChart from './charts/FileTypeDurationChart';
import TopLicenseKeysChart from './charts/TopLicenseKeysChart';

function Analytics() {
    const [runs, setRuns] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [facilitiesError, setFacilitiesError] = useState(null);
    const [days, setDays] = useState(14);
    const [selectedFacility, setSelectedFacility] = useState('');
    const [selectedRunId, setSelectedRunId] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedLicenseKey, setSelectedLicenseKey] = useState('');

    useEffect(() => {
        loadFacilities();
    }, []);

    useEffect(() => {
        loadRuns();
    }, [days, selectedFacility]);

    const loadFacilities = async () => {
        try {
            const data = await extractLogsService.getFacilities();
            setFacilities(data);
            setFacilitiesError(null);
        } catch (err) {
            setFacilitiesError(`Couldn't load the facility list: ${getErrorMessage(err)}`);
        }
    };

    const loadRuns = async () => {
        try {
            setLoading(true);
            const data = await extractLogsService.getRuns(days, selectedFacility || null);
            setRuns(data);
            setError(null);
        } catch (err) {
            setError(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    // Filter runs based on selections
    let filteredRuns = runs;
    if (selectedStatus) {
        filteredRuns = filteredRuns.filter(r => r.status === selectedStatus);
    }
    if (selectedDate) {
        filteredRuns = filteredRuns.filter(r => {
            const runDate = formatDay(r.startTime);
            return runDate === selectedDate;
        });
    }

    // Get completed runs for the run selector
    const completedRuns = filteredRuns.filter(r => r.status === 'Complete');

    // Handle chart clicks
    const handleStatusClick = (status) => {
        setSelectedStatus(selectedStatus === status ? '' : status);
    };

    const handleFacilityClick = (facilityId) => {
        // Find full facility ID from short name (could be just the number like "136951")
        const fullFacility = facilities.find(f => f.includes(facilityId)) || '';
        setSelectedFacility(selectedFacility === fullFacility ? '' : fullFacility);
    };

    const handleDateClick = (date) => {
        setSelectedDate(selectedDate === date ? '' : date);
    };

    const handleLicenseKeyClick = (licenseKey) => {
        setSelectedLicenseKey(selectedLicenseKey === licenseKey ? '' : licenseKey);
    };

    const clearFilters = () => {
        setSelectedFacility('');
        setSelectedStatus('');
        setSelectedDate('');
        setSelectedRunId('');
        setSelectedLicenseKey('');
    };

    const hasActiveFilters = selectedFacility || selectedStatus || selectedDate || selectedRunId || selectedLicenseKey;

    if (loading && runs.length === 0) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <div className="loading-text">Loading analytics...</div>
            </div>
        );
    }

    if (error) {
        return <ErrorState title="Couldn't load analytics" message={error} onRetry={loadRuns} />;
    }

    return (
        <div className="analytics-page">
            <ErrorBanner
                message={facilitiesError}
                onRetry={loadFacilities}
                onDismiss={() => setFacilitiesError(null)}
            />

            <div className="page-header">
                <h1>Analytics</h1>
                {hasActiveFilters && (
                    <button className="btn btn-ghost" onClick={clearFilters}>
                        ✕ Clear Filters
                    </button>
                )}
            </div>

            {/* Active Filters Display */}
            {hasActiveFilters && (
                <div className="active-filters">
                    <span className="filter-label">Active Filters:</span>
                    {selectedFacility && (
                        <span className="filter-tag" onClick={() => setSelectedFacility('')}>
                            Facility: {selectedFacility} ✕
                        </span>
                    )}
                    {selectedStatus && (
                        <span className="filter-tag" onClick={() => setSelectedStatus('')}>
                            Status: {selectedStatus} ✕
                        </span>
                    )}
                    {selectedDate && (
                        <span className="filter-tag" onClick={() => setSelectedDate('')}>
                            Date: {selectedDate} ✕
                        </span>
                    )}
                    {selectedRunId && (
                        <span className="filter-tag" onClick={() => setSelectedRunId('')}>
                            Run: #{selectedRunId} ✕
                        </span>
                    )}
                    {selectedLicenseKey && (
                        <span className="filter-tag" onClick={() => setSelectedLicenseKey('')}>
                            License Key: ...{selectedLicenseKey.slice(-8)} ✕
                        </span>
                    )}
                </div>
            )}

            <div className="card">
                <div className="filters">
                    <div className="filter-group">
                        <label>Time Period</label>
                        <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
                            <option value={7}>Last 7 days</option>
                            <option value={14}>Last 14 days</option>
                            <option value={30}>Last 30 days</option>
                            <option value={60}>Last 60 days</option>
                            <option value={90}>Last 90 days</option>
                        </select>
                    </div>
                    
                    <div className="filter-group">
                        <label>Facility</label>
                        <select value={selectedFacility} onChange={(e) => setSelectedFacility(e.target.value)}>
                            <option value="">All Facilities</option>
                            {facilities.map(f => (
                                <option key={f} value={f}>{f}</option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>Specific Run (for file type breakdown)</label>
                        <select value={selectedRunId} onChange={(e) => setSelectedRunId(e.target.value)}>
                            <option value="">Select a run...</option>
                            {completedRuns.map(r => (
                                <option key={r.runID} value={r.runID}>
                                    #{r.runID} - {r.facilityID} ({formatDay(r.startTime)})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <p className="chart-interaction-hint">
                💡 Click on chart elements to filter data across all charts
            </p>

            <div className="charts-grid">
                <div className="chart-card">
                    <StatusDonutChart 
                        runs={filteredRuns} 
                        onStatusClick={handleStatusClick}
                        selectedStatus={selectedStatus}
                    />
                </div>
                
                <div className="chart-card wide">
                    <DurationLineChart 
                        runs={filteredRuns}
                        onFacilityClick={handleFacilityClick}
                        onDateClick={handleDateClick}
                        selectedDate={selectedDate}
                    />
                </div>
            </div>

            <div className="charts-grid two-col">
                <div className="chart-card">
                    <FileTypeDurationChart 
                        runId={selectedRunId || null} 
                        facilityFilter={selectedFacility}
                        days={days}
                        selectedDate={selectedDate}
                        filteredRuns={filteredRuns}
                        selectedLicenseKey={selectedLicenseKey}
                    />
                    {selectedRunId && (
                        <p className="chart-hint">
                            Showing breakdown for Run #{selectedRunId}
                        </p>
                    )}
                    {selectedLicenseKey && !selectedRunId && (
                        <p className="chart-hint">
                            Showing data for license key: ...{selectedLicenseKey.slice(-8)}
                        </p>
                    )}
                </div>
                
                <div className="chart-card">
                    <TopLicenseKeysChart 
                        days={days}
                        facilityFilter={selectedFacility}
                        onLicenseKeyClick={handleLicenseKeyClick}
                        selectedDate={selectedDate}
                        filteredRuns={filteredRuns}
                        selectedLicenseKey={selectedLicenseKey}
                    />
                </div>
            </div>

            {/* Summary Stats */}
            <div className="card">
                <h3 className="section-title">Summary Statistics</h3>
                <div className="stats-summary">
                    <div className="stat-item">
                        <span className="stat-label">Total Runs</span>
                        <span className="stat-value">{filteredRuns.length}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Completed</span>
                        <span className="stat-value success">{filteredRuns.filter(r => r.status === 'Complete').length}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Avg Duration</span>
                        <span className="stat-value">
                            {completedRuns.length > 0 
                                ? (completedRuns.reduce((sum, r) => sum + (r.totalMinutes || 0), 0) / completedRuns.length).toFixed(1)
                                : 0} min
                        </span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Max Duration</span>
                        <span className="stat-value warning">
                            {completedRuns.length > 0 
                                ? Math.max(...completedRuns.map(r => r.totalMinutes || 0)).toFixed(1)
                                : 0} min
                        </span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Skipped</span>
                        <span className="stat-value error">{filteredRuns.filter(r => r.status === 'Skipped').length}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Analytics;
